import { ThemeEvents } from '@theme/events';

/**
 * Contextual PDP size-help block.
 *
 * Keeps the WhatsApp link's prefilled message and its variant data
 * attributes in sync with the currently selected variant. No analytics
 * is emitted here — a later phase owns tracking via the `data-boop-*`
 * attributes rendered in `blocks/pdp-support.liquid`.
 */
class PdpSupportComponent extends HTMLElement {
  /** @type {((event: Event) => void) | null} */
  #onVariantUpdate = null;

  connectedCallback() {
    this.#onVariantUpdate = (event) => this.#handleVariantUpdate(event);
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    if (this.#onVariantUpdate) {
      document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
      this.#onVariantUpdate = null;
    }
  }

  /**
   * Rebuilds the WhatsApp prefill and variant data attributes when the
   * matching product's variant changes.
   * @param {Event} event - The `variant:update` event.
   */
  #handleVariantUpdate(event) {
    const detail = /** @type {{ resource: any, data: { productId?: string } }} */ (
      /** @type {CustomEvent} */ (event).detail
    );
    if (!detail || detail.data?.productId !== this.dataset.productId) return;

    const link = this.querySelector('a[data-boop-track="fit_help"]');
    if (!(link instanceof HTMLAnchorElement)) return;

    const variant = detail.resource;
    const productTitle = this.dataset.productTitle ?? '';
    const base = link.href.split('?')[0];

    if (variant && variant.title) {
      link.href = `${base}?text=${encodeURIComponent(`Hi! I need help choosing a size for ${productTitle} (${variant.title})`)}`;
      link.dataset.variantId = variant.id != null ? String(variant.id) : '';

      const sizeIndex = this.dataset.sizeOptionIndex;
      if (sizeIndex != null && sizeIndex !== '' && Array.isArray(variant.options)) {
        link.dataset.selectedSize = variant.options[Number(sizeIndex)] ?? '';
      } else {
        link.dataset.selectedSize = '';
      }
    } else {
      // Variant unavailable — fall back to a generic, product-only prefill.
      link.href = `${base}?text=${encodeURIComponent(`Hi! I need help choosing a size for ${productTitle}`)}`;
      link.dataset.variantId = '';
      link.dataset.selectedSize = '';
    }
  }
}

if (!customElements.get('pdp-support-component')) {
  customElements.define('pdp-support-component', PdpSupportComponent);
}
